import { Drawer as DrawerAntd, Modal as ModalAntd } from "antd";
import type { DrawerProps, ModalProps } from "antd";
import React, { ReactElement, ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ReactDOM from "react-dom/client";
import { WrapperStore } from "../..";
import Header from "../../components/Popup/_components/Header";
import ConfigAnt from "../../components/Theme/ConfigAnt";
import { getParentEl, propsModal } from "../../util/varibles/constants";
import { createFastContext } from "../createFastContext";
import stylesModule from "./style.module.scss";

interface IPropsPopup {
    children: ReactNode,

    onClose?(): void
}

type PopupClassNames = Exclude<DrawerProps['classNames'], Function> | Exclude<ModalProps['classNames'], Function>;
type PopupStyles = Exclude<DrawerProps['styles'], Function> | Exclude<ModalProps['styles'], Function>;

const Modal: React.FC<IPropsPopup> = ({ children, onClose }) => {
    const [modalProps, set] = usePopup(state => state.modalProps);
    const [visible] = usePopup(state => state.visible);

    const { width, style, styles, title, mask = { closable: true }, destroyOnHidden = true, radius = '8px', className, zIndex, classNames } = modalProps || {};

    const handleClose = useCallback(() => {
        set({ visible: false });
        setTimeout(() => onClose?.(), 300);
    }, [onClose, set]);

    return <ModalAntd
        {...propsModal}
        classNames={classNames}
        width={width}
        style={{ ...style, "--radius": radius }}
        className={[stylesModule.popup, className].join(' ')}
        open={visible}
        styles={styles}
        title={title && <Header title={title} onClose={handleClose} />}
        mask={mask}
        destroyOnHidden={destroyOnHidden}
        zIndex={zIndex}
        onCancel={handleClose}
    >
        {React.isValidElement(children)
            ? React.cloneElement(children as ReactElement, { onClose: handleClose })
            : children}
    </ModalAntd>
}

const Drawer: React.FC<IPropsPopup> = ({ children, onClose }) => {
    const [modalProps, set] = usePopup(state => state.modalProps);
    const [visible] = usePopup(state => state.visible);

    const { width, style, title, styles, destroyOnHidden = true, className, zIndex, classNames } = modalProps || {};

    const handleClose = useCallback(() => {
        set({ visible: false });
        setTimeout(() => onClose?.(), 300);
    }, [onClose, set]);

    return <DrawerAntd
        {...propsModal}
        classNames={classNames}
        size={width}
        style={style}
        className={[stylesModule.popup, className].join(' ')}
        open={visible}
        styles={styles}
        title={title && <Header title={title} onClose={handleClose} />}
        mask={{ closable: true }}
        destroyOnHidden={destroyOnHidden}
        zIndex={zIndex}
        onClose={handleClose}
    >
        {React.isValidElement(children)
            ? React.cloneElement(children as ReactElement, { onClose: handleClose })
            : children}
    </DrawerAntd>
}

interface IState {
    visible?: boolean
    children?: ReactNode

    modalProps?: {
        destroyOnHidden?: boolean
        style?: any
        width?: number | string
        radius?: number | string
        className?: string
        classNames?: PopupClassNames
        title?: string | ReactNode
        styles?: PopupStyles
        zIndex?: number
        mask?: DrawerProps['mask']
        closable?: boolean
    }

    onClose?(): void
}

const {
    Provider,
    useStore: usePopup,
    setStore: setPopup,
    getStore: getPopup,
    callEvent: callEventPopup
} = createFastContext<IState, {
    onClose(e?: React.MouseEvent): void
}>({});

export enum POPUP_TYPE {
    MODAL = "MODAL",
    DRAWER = "DRAWER"
}

const PopupProvider = ({ children, ...props }: {
    children: ReactNode,
    popupType?: POPUP_TYPE
} & Partial<IState>) => {
    const { popupType = POPUP_TYPE.MODAL, onClose, ...args } = props;
    const Popup = useMemo(() => popupType === POPUP_TYPE.MODAL ? Modal : Drawer, [popupType]);

    return <Provider
        props={{ ...args, visible: true }}
        event={{
            onClose: (e, props) => {
                const { set } = { ...e, ...props };
                onClose?.();
                set({ visible: false });
            }
        }} >
        <Popup onClose={onClose}>{children}</Popup>
    </Provider>
}

interface IPropsOpenPopup {
    Wrapper?: React.FC<any>
    popupType?: POPUP_TYPE
}

export type TOpenPopup2 = (
    children: ReactNode,
    props?: Partial<IPropsOpenPopup & IState>
) => void;

export function openPopup2(children: ReactNode, props?: Partial<(IPropsOpenPopup & IState)>): HTMLDivElement {
    const el = document.createElement('div');
    const root = ReactDOM.createRoot(el as HTMLElement);
    const { onClose, Wrapper = WrapperStore, visible = true, ...args } = props || {};

    const handleClose = () => {
        onClose?.();
        el.remove();
    }

    root.render(<Wrapper>
        <ConfigAnt>
            <PopupProvider {...args} visible={visible} onClose={handleClose}>
                {children}
            </PopupProvider>
        </ConfigAnt>
    </Wrapper>);

    const container = getParentEl();
    container.append(el);
    return el;
}

export function usePopupPortal() {
    const idRef = useRef(0);
    const [items, setItems] = useState<Array<{
        id: number;
        children: ReactNode;
        props?: Partial<IPropsOpenPopup & IState>;
    }>>([]);

    const openPopup: TOpenPopup2 = useCallback((children, props) => {
        idRef.current += 1;
        setItems(prev => [...prev, {
            id: idRef.current,
            children,
            props,
        }]);
    }, []);

    const popupHolder = <>
        {items.map(({ id, children, props }) => {
            const { onClose, visible = true, ...args } = props || {};
            const handleClose = () => {
                onClose?.();
                setItems(prev => prev.filter(item => item.id !== id));
            };

            return createPortal(
                <ConfigAnt>
                    <PopupProvider {...args} visible={visible} onClose={handleClose}>
                        {children}
                    </PopupProvider>
                </ConfigAnt>,
                getParentEl(),
                id,
            );
        })}
    </>;

    return { openPopup, popupHolder };
}

export { callEventPopup, getPopup, PopupProvider, setPopup, usePopup };
